import { MDXRemote } from "next-mdx-remote/rsc";
import type { ComponentProps, ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/utils";

import { SelectIcon } from "./icons/select-icon";
import { InlineLink } from "./ui/link";

type RichTextComponents = ComponentProps<typeof MDXRemote>["components"];

const INTERNAL_DOMAIN_REGEX = /^https?:\/\/(www\.)?wayflyer\.com/;

export const richTextComponents = {
  SelectIcon,
  a: ({ href, children }: { href?: string; children: React.ReactNode }) => {
    if (!href) return <>{children}</>;
    const isInternal = INTERNAL_DOMAIN_REGEX.test(href) || href.startsWith("/");
    const path = isInternal ? href.replace(INTERNAL_DOMAIN_REGEX, "") : href;
    return (
      <InlineLink
        href={path}
        external={!isInternal}
        {...(href.startsWith("https://")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {children}
      </InlineLink>
    );
  },
  sup: ({ children }: { children: React.ReactNode }) => (
    <sup className="top-px align-super">{children}</sup>
  ),
  // Pull quotes in long-form Markdown. Without this mapping a `>` block
  // renders as a bare <blockquote>, which Preflight strips to look exactly
  // like body copy. Matches the treatment in `lib/content/mdx-components-v2`.
  blockquote: ({
    className,
    ...props
  }: ComponentPropsWithoutRef<"blockquote">) => (
    <blockquote
      className={cn(
        "text-grey-950 text-ds-heading-h4 border-grey-100 my-8 border-l-2 py-2 pl-6 italic",
        className
      )}
      {...props}
    />
  ),
};

export function RichText({
  value,
  components,
}: {
  value: string;
  /** Optional overrides merged over the defaults (e.g. a locale-free link renderer). */
  components?: RichTextComponents;
}) {
  return (
    <MDXRemote
      source={value}
      components={{ ...richTextComponents, ...components }}
    />
  );
}
